import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from 'ag-grid-community';
import { useLocation } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Flex,
  Heading,
  SelectField,
  Text,
  TextField,
  View,
} from '@aws-amplify/ui-react';
import Loadingmini from '../components/loadingmini';
import { api3Get, api3Post } from '../api/api3Client';

function fleetsQuery(selectedFleetCode) {
  const query = {};
  if (selectedFleetCode) query.fleetCode = selectedFleetCode;
  return query;
}

function fmtRate(val) {
  if (val === null || val === undefined || val === '' || Number.isNaN(Number(val))) return '-';
  return `${Number(val)}%`;
}

function localityKey(loc) {
  if (loc?.key) return loc.key;
  return [loc?.state || '', loc?.county || '', loc?.city || '', loc?.regionId ?? ''].join('|');
}

function regionKey(r) {
  return r.regionId == null ? 'none' : String(r.regionId);
}

function stackLabel(taxes) {
  if (!taxes || taxes.length === 0) return 'No tax';
  return taxes
    .map((t) => {
      const timed = t.taxType && String(t.taxType).includes('timed') ? ` after ${t.daysNoTax}d` : '';
      return `${t.taxName} ${fmtRate(t.rate)} (${t.jurisdiction}${timed})`;
    })
    .join(' · ');
}

const emptyForm = {
  jurisdiction: 'county',
  state: '',
  county: '',
  city: '',
  taxName: 'Sales Tax',
  taxType: 'rate',
  rate: '',
  daysNoTax: '',
  dateStart: '',
};

export default function Fleettaxes() {
  const location = useLocation();
  const [selectedFleetCode, setSelectedFleetCode] = useState(null);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [rateEdit, setRateEdit] = useState(null);
  const [newRate, setNewRate] = useState('');
  const [selectedKind, setSelectedKind] = useState(null);
  const [selectedAddressKey, setSelectedAddressKey] = useState(null);
  const [selectedTaxId, setSelectedTaxId] = useState(null);
  const addressGridRef = useRef(null);
  const catalogGridRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fleetsParam = params.get('fleets');
    setSelectedFleetCode(fleetsParam ? fleetsParam.split(',')[0] || null : null);
    setRegionFilter('all');
    setSelectedKind(null);
    setSelectedAddressKey(null);
    setSelectedTaxId(null);
  }, [location.search]);

  async function load() {
    if (!selectedFleetCode) {
      setData(null);
      setStatusMessage('Select a fleet to view taxes.');
      return;
    }
    setIsLoading(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await api3Get('/fleets/locality-taxes', fleetsQuery(selectedFleetCode));
      setData(res);
    } catch (e) {
      setError(e.message || String(e));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFleetCode]);

  const canWrite = Boolean(data?.canWrite);
  const regions = data?.regions || [];
  const localities = data?.localities || [];
  const totalCars = useMemo(
    () => localities.reduce((sum, r) => sum + Number(r.vehicleCount || 0), 0),
    [localities]
  );

  const visibleAddresses = useMemo(() => {
    if (regionFilter === 'all') return localities;
    if (regionFilter === 'none') return localities.filter((g) => g.regionId == null);
    const rid = Number(regionFilter);
    return localities.filter((g) => g.regionId === rid);
  }, [localities, regionFilter]);

  const catalogTaxes = useMemo(
    () => (data?.taxes || []).filter((t) => t.isCurrent),
    [data]
  );

  const visibleGaps = useMemo(
    () => visibleAddresses.filter((a) => !a.covered),
    [visibleAddresses]
  );

  useEffect(() => {
    addressGridRef.current?.api?.redrawRows?.();
    catalogGridRef.current?.api?.redrawRows?.();
  }, [selectedKind, selectedAddressKey, selectedTaxId, visibleAddresses, catalogTaxes]);

  const addressRowClass = useCallback(
    (p) => {
      const key = localityKey(p.data);
      const classes = [];
      if (selectedKind === 'address' && selectedAddressKey === key) classes.push('tax-row-selected');
      if (selectedKind === 'tax' && selectedTaxId != null && (p.data.appliedTaxIds || []).includes(selectedTaxId)) {
        classes.push('tax-row-match');
      }
      if (!p.data.covered) classes.push('tax-row-gap');
      return classes.join(' ');
    },
    [selectedKind, selectedAddressKey, selectedTaxId]
  );

  const catalogRowClass = useCallback(
    (p) => {
      const classes = [];
      if (selectedKind === 'tax' && selectedTaxId === p.data.id) classes.push('tax-row-selected');
      if (selectedKind === 'address' && selectedAddressKey) {
        const addr = localities.find((a) => localityKey(a) === selectedAddressKey);
        if (addr && (addr.appliedTaxIds || []).includes(p.data.id)) classes.push('tax-row-match');
      }
      if ((p.data.warnings || []).length) classes.push('tax-row-gap');
      return classes.join(' ');
    },
    [selectedKind, selectedAddressKey, selectedTaxId, localities]
  );

  function prefillFromAddress(addr) {
    setForm({
      ...emptyForm,
      jurisdiction: 'county',
      state: addr.state || '',
      county: addr.county || '',
      city: addr.city || '',
      taxName: 'Sales Tax',
      taxType: 'rate',
      rate: '',
    });
  }

  function onAddressClicked(row) {
    setSelectedKind('address');
    setSelectedAddressKey(localityKey(row));
    setSelectedTaxId(null);
    setRateEdit(null);
    if (canWrite) prefillFromAddress(row);
  }

  function onCatalogClicked(row) {
    setSelectedKind('tax');
    setSelectedTaxId(row.id);
    setSelectedAddressKey(null);
    if (canWrite) {
      setRateEdit(row);
      setNewRate(row.rate != null ? String(row.rate) : '');
    }
  }

  async function createTax(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setStatusMessage('');
    try {
      const body = {
        action: 'create',
        fleetCode: selectedFleetCode,
        jurisdiction: form.jurisdiction,
        state: form.state,
        county: form.county || undefined,
        city: form.city || undefined,
        taxName: form.taxName,
        taxType: form.taxType,
        rate: Number(form.rate),
        daysNoTax: form.taxType === 'rate_timed' && form.daysNoTax !== '' ? Number(form.daysNoTax) : undefined,
        dateStart: form.dateStart || undefined,
      };
      const res = await api3Post('/fleets/locality-taxes', body, fleetsQuery(selectedFleetCode));
      setData(res);
      setForm(emptyForm);
      setStatusMessage(`Added tax (Stripe ${res?.created?.stripeId || 'ok'}).`);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function terminateTax(row) {
    if (!window.confirm(`End ${row.taxName} for ${row.city || row.county || row.state}? New invoices will stop using it.`)) {
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await api3Post(
        '/fleets/locality-taxes',
        { action: 'terminate', taxId: row.id, fleetCode: selectedFleetCode },
        fleetsQuery(selectedFleetCode)
      );
      setData(res);
      setRateEdit(null);
      setStatusMessage('Tax ended (Stripe rate archived).');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitRateEdit(e) {
    e.preventDefault();
    if (!rateEdit) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await api3Post(
        '/fleets/locality-taxes',
        {
          action: 'update',
          taxId: rateEdit.id,
          fleetCode: selectedFleetCode,
          rate: Number(newRate),
        },
        fleetsQuery(selectedFleetCode)
      );
      setData(res);
      setRateEdit(null);
      setNewRate('');
      setStatusMessage('New rate saved (new Stripe tax rate; old rate archived).');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const addressColumns = [
    { headerName: 'City', field: 'city', width: 150 },
    { headerName: 'County', field: 'county', width: 140 },
    { headerName: 'State', field: 'state', width: 130 },
    { headerName: 'Region', field: 'regionName', width: 130 },
    { headerName: 'Cars', field: 'vehicleCount', width: 90, type: 'numericColumn' },
    {
      headerName: 'Invoice stack',
      flex: 1,
      minWidth: 280,
      wrapText: true,
      autoHeight: true,
      valueGetter: (p) => stackLabel(p.data.appliedTaxes),
    },
    {
      headerName: 'Notes',
      flex: 1,
      minWidth: 220,
      wrapText: true,
      autoHeight: true,
      valueGetter: (p) => (p.data.warnings || []).join(' · ') || '',
    },
  ];

  const catalogColumns = [
    { headerName: 'Tax', field: 'taxName', width: 130 },
    { headerName: 'Type', field: 'taxType', width: 120 },
    { headerName: 'Jurisdiction', field: 'jurisdiction', width: 120 },
    { headerName: 'State', field: 'state', width: 130 },
    { headerName: 'County', field: 'county', width: 140 },
    { headerName: 'City', field: 'city', width: 140 },
    { headerName: 'Rate', valueGetter: (p) => fmtRate(p.data.rate), width: 90 },
    { headerName: 'Start', field: 'dateStart', width: 120 },
    { headerName: 'Cars hit', field: 'matchedVehicleCount', width: 110, type: 'numericColumn' },
    { headerName: 'On invoices', valueGetter: (p) => (p.data.invoiceEffective ? 'yes' : 'no'), width: 120 },
    { headerName: 'Stripe', field: 'stripeId', minWidth: 160, flex: 1 },
    {
      headerName: 'Notes',
      minWidth: 220,
      flex: 1,
      wrapText: true,
      autoHeight: true,
      valueGetter: (p) => (p.data.warnings || []).join(' · ') || '',
    },
  ];

  return (
    <Flex direction="column" gap="1rem" padding="1rem">
      <Heading level={4}>Locality taxes</Heading>
      <Text>
        Taxes match invoices by city / county / state — not by region. The address list is what a
        new invoice would charge. The catalog is the fleet tax rows you edit. Region is a label
        and optional filter only.
      </Text>
      {!selectedFleetCode ? <Text>Select a fleet.</Text> : null}
      {isLoading ? <Loadingmini /> : null}
      {error ? <Alert variation="error">{error}</Alert> : null}
      {statusMessage ? <Alert variation="success">{statusMessage}</Alert> : null}

      {data ? (
        <>
          <Text>
            {data.fleetName || data.fleetCode}
            {data.stripeMode ? ` · Stripe ${data.stripeMode}` : ''}
            {canWrite ? '' : ' · view only'}
            {` · ${totalCars} non-retired car${totalCars === 1 ? '' : 's'}`}
            {data.asOf ? ` · invoice match as of ${data.asOf}` : ''}
          </Text>
          <Flex wrap="wrap" gap="0.5rem" alignItems="center">
            <Button size="small" variation={regionFilter === 'all' ? 'primary' : undefined} onClick={() => setRegionFilter('all')}>
              All regions ({totalCars} cars)
            </Button>
            {regions.map((r) => (
              <Button
                key={regionKey(r)}
                size="small"
                variation={regionFilter === regionKey(r) ? 'primary' : undefined}
                onClick={() => setRegionFilter(regionKey(r))}
              >
                {r.regionName} ({r.vehicleCount} car{r.vehicleCount === 1 ? '' : 's'}
                {r.gapCount ? `, ${r.gapCount} gap` : ''})
              </Button>
            ))}
            <Button size="small" onClick={load} disabled={isLoading}>Refresh</Button>
          </Flex>

          {visibleGaps.length > 0 ? (
            <Alert variation="warning">
              {visibleGaps.length} address{visibleGaps.length === 1 ? '' : 'es'} with no tax on invoices.
              Click a row to prefill Add tax.
            </Alert>
          ) : null}

          <Heading level={5}>Addresses</Heading>
          <Text fontSize="0.85em">
            <i>Click an address to highlight the catalog rows that would apply. Region filter only changes this list.</i>
          </Text>
          <div className="ag-theme-quartz" style={{ width: '100%', height: Math.max(180, 56 + visibleAddresses.length * 42) }}>
            <AgGridReact
              ref={addressGridRef}
              theme={themeQuartz}
              rowData={visibleAddresses}
              columnDefs={addressColumns}
              getRowId={(p) => localityKey(p.data)}
              getRowClass={addressRowClass}
              onRowClicked={(e) => onAddressClicked(e.data)}
              overlayNoRowsTemplate="No non-retired cars with an address"
            />
          </div>

          <Heading level={5}>Tax catalog</Heading>
          <Text fontSize="0.85em">
            <i>All current fleet tax rows, including ones that hit zero cars. Click a row to change the rate or end it.</i>
          </Text>
          <div className="ag-theme-quartz" style={{ width: '100%', height: Math.max(220, 56 + Math.min(catalogTaxes.length, 12) * 42) }}>
            <AgGridReact
              ref={catalogGridRef}
              theme={themeQuartz}
              rowData={catalogTaxes}
              columnDefs={catalogColumns}
              getRowId={(p) => String(p.data.id)}
              getRowClass={catalogRowClass}
              onRowClicked={(e) => onCatalogClicked(e.data)}
              overlayNoRowsTemplate="No current tax rows"
            />
          </div>

          {canWrite && rateEdit ? (
            <View>
              <Heading level={5}>
                Edit {rateEdit.taxName} · {rateEdit.city || rateEdit.county || rateEdit.state}
              </Heading>
              <form onSubmit={submitRateEdit}>
                <Flex gap="0.75rem" wrap="wrap" alignItems="flex-end">
                  <TextField
                    label="New rate (%)"
                    value={newRate}
                    onChange={(e) => setNewRate(e.target.value)}
                    type="number"
                    step="0.01"
                    required
                  />
                  <Button type="submit" size="small" disabled={submitting}>Save new rate</Button>
                  <Button type="button" size="small" variation="destructive" disabled={submitting} onClick={() => terminateTax(rateEdit)}>
                    End tax
                  </Button>
                  <Button type="button" size="small" variation="link" onClick={() => setRateEdit(null)}>Cancel</Button>
                </Flex>
              </form>
              <Text fontSize="0.85em">
                Stripe cannot change a tax rate percentage in place. Save creates a new Stripe rate and a new SQL row from today.
              </Text>
            </View>
          ) : null}

          {canWrite ? (
            <View>
              <Heading level={5}>Add tax</Heading>
              <form onSubmit={createTax}>
                <Flex direction="column" gap="0.75rem" maxWidth="42rem">
                  <SelectField
                    label="Jurisdiction"
                    value={form.jurisdiction}
                    onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
                  >
                    {(data.jurisdictions || ['state', 'county', 'city']).map((j) => (
                      <option key={j} value={j}>{j}</option>
                    ))}
                  </SelectField>
                  <TextField label="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} required />
                  {form.jurisdiction !== 'state' ? (
                    <TextField label="County" value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} required />
                  ) : null}
                  {form.jurisdiction === 'city' ? (
                    <TextField label="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required />
                  ) : null}
                  <SelectField
                    label="Tax name"
                    value={form.taxName}
                    onChange={(e) => setForm({ ...form, taxName: e.target.value })}
                  >
                    {(data.taxNames || []).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </SelectField>
                  <SelectField
                    label="Type"
                    value={form.taxType}
                    onChange={(e) => setForm({ ...form, taxType: e.target.value })}
                  >
                    <option value="rate">rate</option>
                    <option value="rate_timed">rate_timed (days before tax)</option>
                  </SelectField>
                  <TextField
                    label="Rate (%)"
                    value={form.rate}
                    onChange={(e) => setForm({ ...form, rate: e.target.value })}
                    type="number"
                    step="0.01"
                    required
                  />
                  {form.taxType === 'rate_timed' ? (
                    <TextField
                      label="Days before tax applies"
                      value={form.daysNoTax}
                      onChange={(e) => setForm({ ...form, daysNoTax: e.target.value })}
                      type="number"
                    />
                  ) : null}
                  <TextField
                    label="Start date (optional, default today)"
                    value={form.dateStart}
                    onChange={(e) => setForm({ ...form, dateStart: e.target.value })}
                    type="date"
                  />
                  <Button type="submit" disabled={submitting}>Create tax + Stripe rate</Button>
                </Flex>
              </form>
              <Text fontSize="0.85em">
                Start date must be before the invoice date to apply (today means it starts tomorrow).
                Spell city/county the same as vehicle addresses.
              </Text>
            </View>
          ) : (
            <Text><i>Support is view-only. Admin or fleet can add and end taxes.</i></Text>
          )}
        </>
      ) : null}
    </Flex>
  );
}
